import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { getCurrentUserId } from '../../user/currentUserSelector';
import { getTaskItemChipDescriptors } from './taskItemChipsUtils';
import type { TaskItemChipSource, UpdateTaskItemAttrs } from './taskItemChipsUtils';
import { useElementChipCallbacks } from './hooks/useElementChipCallbacks';
import { useElementChipSelection } from './hooks/useElementChipSelection';
import { useElementChipEditing } from './hooks/useElementChipEditing';
import { useAssignmentTaskItemChipCallbacks } from './hooks/useAssignmentTaskItemChipCallbacks';
import { useDueDateTaskItemChipCallbacks } from './hooks/useDueDateTaskItemChipCallbacks';
import { useLabelTaskItemChipCallbacks } from './hooks/useLabelTaskItemChipCallbacks';
import { useReactionTaskItemChipCallbacks } from './hooks/useReactionTaskItemChipCallbacks';
import ElementChip from './ElementChip';
import ElementChipInteractionContainer from './ElementChipInteractionContainer';
import { ChipBaseProps } from '../../../common/@roma/src/components/base/chip/ChipBase';

type TaskItemChipsContainerProps = {
    /** The owning task node's chip-relevant attrs (plus `checked`). */
    attrs: TaskItemChipSource;
    /** Updates the owning task node's attrs (the node view's `updateAttributes`). */
    updateAttributes: UpdateTaskItemAttrs;
    /** Whether chips can be modified. Defaults to true for the Storybook POC (no permission context). */
    canModify?: boolean;
    size: ChipBaseProps['size'];
};

/**
 * Renders the data-based chips for a single Tiptap task item, reusing the shared
 * chip views, selection and editing layers while mutating node attrs (rather than
 * dispatching element-keyed redux actions) for every interaction.
 */
const TaskItemChipsContainer: React.FC<TaskItemChipsContainerProps> = ({
    attrs,
    updateAttributes,
    canModify = true,
    size = 'xs',
}) => {
    const [editingChipId, setEditingChipId] = useState<string | null>(null);
    const currentUserId = useSelector(getCurrentUserId);

    // The taskId keys global single-chip selection across the whole task list.
    const containerId = attrs.taskId ?? '';

    const descriptors = useMemo(() => getTaskItemChipDescriptors(attrs), [attrs]);

    const labelCallbackSet = useLabelTaskItemChipCallbacks({
        labels: attrs.labels,
        updateAttributes,
        canModifyLabels: canModify,
    });
    const reactionCallbackSet = useReactionTaskItemChipCallbacks({
        reactions: attrs.reactions,
        currentUserId,
        updateAttributes,
        canModifyReactions: canModify,
    });
    const assignmentCallbackSet = useAssignmentTaskItemChipCallbacks({
        updateAttributes,
        canModifyAssignments: canModify,
    });
    const dueDateCallbackSet = useDueDateTaskItemChipCallbacks({
        updateAttributes,
        canModifyDueDate: canModify,
    });
    const callbackSets = useMemo(
        () => [labelCallbackSet, reactionCallbackSet, assignmentCallbackSet, dueDateCallbackSet],
        [assignmentCallbackSet, dueDateCallbackSet, labelCallbackSet, reactionCallbackSet],
    );
    const callbackEnrichedChips = useElementChipCallbacks({ chips: descriptors, callbackSets });
    const clearEditing = useCallback(() => setEditingChipId(null), []);

    // A focused task item is always the active surface, so select-mode chips are
    // always activatable (there is no board single-selection gate here).
    const selectionResult = useElementChipSelection({
        chips: callbackEnrichedChips,
        elementId: containerId,
        selectedElementId: containerId,
        isElementSingleSelected: true,
        clearEditing,
    });

    const interactiveChips = useElementChipEditing({
        chips: selectionResult.chips,
        editingChipId,
        setEditingChipId,
    });

    if (!interactiveChips.length) return null;

    return (
        <div className="TaskItemChipsContainer flex flex-row flex-wrap gap-sm">
            {interactiveChips.map((chip) => {
                const { interaction, ...chipProps } = chip;

                return (
                    <ElementChipInteractionContainer key={chip.id} chip={chip}>
                        <ElementChip
                            {...chipProps}
                            {...interaction}
                            size={size}
                            currentUserId={currentUserId ?? undefined}
                        />
                    </ElementChipInteractionContainer>
                );
            })}
        </div>
    );
};

export default TaskItemChipsContainer;
